import * as React from 'react';
import styled from 'styled-components';
import FileUploadIcon from '../../icons/accent/FileUploadIcon';
import { useIntlUds } from '../../translations/UdsIntlProvider';
import { tokens } from '../../theme';

export interface FileUploadBoxProps {
  /**
   * **REQUIRED** - Set to optional for backwards compatibility.
   * A screen with more than one FileUploadBox element will not work properly without this prop.
   */
  inputId?: string;
  onUpload?(file?: File): void;
  /** Only expected to trigger if `allowedExtensions` list unsupported by browser or bypassed. */
  onError?(reason?: string): void;
  /** Has default value in English. */
  instructionHeader?: string;
  description?: string;
  fileSizeLimitMessage?: string;
  padding?: string;
  icon?: JSX.Element;
  allowedExtensions?: `.${string}`[];
  dataTest?: string;
  maxBytes?: number;
}

export const FileUploadBox: React.FC<React.PropsWithChildren<FileUploadBoxProps>> = ({
  inputId = 'uploadFile',
  onUpload,
  onError,
  instructionHeader,
  description,
  padding,
  icon,
  allowedExtensions,
  dataTest,
  fileSizeLimitMessage = 'File size exceeds limit',
  maxBytes
}) => {
  const { intl } = useIntlUds();
  const [drag, setDrag] = React.useState(false);
  const allowedExtensionsInput = allowedExtensions?.join(', ');
  const allowedExtensionsRegex = React.useMemo(
    () => new RegExp(`(${allowedExtensions?.join('|\\') ?? ''})$`, 'gi'),
    [allowedExtensions]
  );

  const onUploadClick = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        if (maxBytes && file?.size && file?.size > maxBytes) {
          onError?.(fileSizeLimitMessage);
        } else if (file?.name && allowedExtensionsRegex.test(file.name)) {
          onUpload?.(file);
        } else {
          onError?.();
        }
      }
    },
    [allowedExtensionsRegex, onError, maxBytes, fileSizeLimitMessage, onUpload]
  );

  const dropRef = React.createRef<HTMLDivElement>();

  const handleDrag = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDrag(true);
    }
  }, []);

  const handleDragOut = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(false);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDrag(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        if (file?.name && allowedExtensionsRegex.exec(file.name)) {
          onUpload?.(file);
        } else {
          onError?.();
        }
      }
    },
    [allowedExtensionsRegex, onError, onUpload]
  );

  const handleInputClick = React.useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.value = '';
  }, []);

  return (
    <StyledLabel htmlFor={inputId} data-test={dataTest}>
      <StyledDropBox
        $padding={padding}
        ref={dropRef}
        onDragOver={handleDrag}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDrop={handleDrop}
        $isDragging={drag}
      >
        <StyledUploadInput
          id={inputId}
          type="file"
          accept={allowedExtensionsInput}
          onChange={onUploadClick}
          onClick={handleInputClick}
        />
        {icon || <FileUploadIcon />}
        {<StyledHeader>{instructionHeader ?? intl.FileUploadBox.instructionHeader}</StyledHeader>}
        {description && <StyledDescription>{description}</StyledDescription>}
      </StyledDropBox>
    </StyledLabel>
  );
};

export default FileUploadBox;

const StyledHeader = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.font.sizeSmall};
  text-align: center;
  margin: ${({ theme }) => theme.spacingSize.m} 0;
  color: ${(props) => props.theme.colorOnSurface};
  pointer-events: none;
`;

const StyledDescription = styled.div`
  font-size: ${({ theme }) => theme.font.sizeXXSmall};
  text-align: center;
  color: ${(props) => props.theme.colorOnSurfaceVariant};
`;

/**
 * Undisplayed input element is still intractable via its label.
 *
 * Other methods of hiding the element can break the styling of certain screens.
 */
const StyledUploadInput = styled.input`
  display: none;
`;

const StyledLabel = styled.label`
  cursor: pointer;
`;

/* This component should be reconciled with QuickStart/DropBox.tsx */
const StyledDropBox = styled.div<{ $padding?: string; $isDragging?: boolean }>`
  min-height: 250px;
  margin: 1.5% 0px;
  z-index: 3;
  background-color: ${tokens.colorSurface};
  width: 100%;
  padding: ${({ $padding }) => $padding || '2rem'};
  border: ${({ $isDragging }) => ($isDragging ? `2px` : `1px`)} dashed
    ${({ $isDragging }) => ($isDragging ? tokens.colorPrimary : tokens.colorOutline)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ $isDragging }) => ($isDragging ? tokens.shadowModerate : `none`)};
`;
